import React, { useEffect, useMemo, useState } from "react";
import api from "../auth/api";

function fmtDate(dateStr) {
  if (!dateStr) return "";
  try {
    const d = new Date(dateStr);
    if (Number.isNaN(d.getTime())) return String(dateStr).slice(0, 10);
    return d.toLocaleDateString();
  } catch {
    return String(dateStr).slice(0, 10);
  }
}

function fmtMoney(v) {
  const n = Number(v || 0);
  return n.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

export default function DocumentosClienteModal({
  open,
  onClose,
  contratoDefault = "", // aquí llega el número de contrato, pero lo mandaremos como recibo
  clienteDefault = "",
}) {
  const [recibo, setRecibo] = useState(String(contratoDefault || ""));
  const [cliente, setCliente] = useState(String(clienteDefault || ""));
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && onClose?.();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  useEffect(() => {
    if (!open) return;
    setErr("");
    setRows([]);
    setRecibo(String(contratoDefault || ""));
    setCliente(String(clienteDefault || ""));
  }, [open, contratoDefault, clienteDefault]);

  const canQuery = useMemo(() => !!String(recibo || "").trim(), [recibo]);

  const fetchDocs = async () => {
    if (!canQuery) return;

    setLoading(true);
    setErr("");

    try {
      const { data } = await api.get("/documentos-cliente", {
        params: {
          recibo: String(recibo || "").trim(),
          cliente: String(cliente || "").trim() || undefined,
        },
      });

      setRows(data?.rows || []);
    } catch (e) {
      setRows([]);
      setErr(e?.response?.data?.error || e?.message || "Error consultando documentos");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!open || !canQuery) return;

    const t = setTimeout(() => {
      fetchDocs();
    }, 250);

    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, recibo, cliente]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center" role="dialog" aria-modal="true">
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-[2px]"
        onClick={() => onClose?.()}
      />

      <div className="relative w-[1180px] max-w-[96vw] rounded-2xl bg-white shadow-2xl ring-1 ring-black/10 overflow-hidden animate-[modalIn_.14s_ease-out]">
        <div className="px-5 py-4 border-b bg-gradient-to-r from-slate-50 to-white flex items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="font-extrabold text-lg text-slate-900">
              Documentos del Cliente
            </div>
            <div className="text-sm text-slate-600 mt-0.5 truncate">
              Consulta por contrato
              {rows?.length ? <> · Registros: <b className="text-slate-900">{rows.length}</b></> : null}
            </div>
          </div>

          <div className="flex gap-2 shrink-0">
            <button
              type="button"
              className="px-3 py-1.5 rounded-xl border border-slate-200 text-slate-900 hover:bg-slate-50 active:scale-[.98]"
              onClick={fetchDocs}
              title="Refrescar"
              disabled={loading || !canQuery}
            >
              ⟳
            </button>

            <button
              type="button"
              className="px-3 py-1.5 rounded-xl border border-slate-200 text-slate-900 hover:bg-slate-50 active:scale-[.98]"
              onClick={() => onClose?.()}
              title="Cerrar"
            >
              ✕
            </button>
          </div>
        </div>

        <div className="px-5 py-4 border-b bg-white">
          <div className="grid grid-cols-12 gap-3">
            <div className="col-span-12 md:col-span-4">
              <label className="text-xs font-extrabold text-slate-700">Contrato *</label>
              <input
                value={recibo}
                onChange={(e) => setRecibo(e.target.value.replace(/[^\d]/g, ""))}
                placeholder="Ej: 39774"
                className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-slate-900 outline-none focus:ring-4 focus:ring-blue-100 focus:border-blue-300"
              />
            </div>

            <div className="col-span-12 md:col-span-4">
              <label className="text-xs font-extrabold text-slate-700">Cliente</label>
              <input
                value={cliente}
                onChange={(e) => setCliente(e.target.value)}
                placeholder="Opcional"
                className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-slate-900 outline-none focus:ring-4 focus:ring-blue-100 focus:border-blue-300"
              />
            </div>

            <div className="col-span-12 md:col-span-4 flex items-end">
              <button
                type="button"
                onClick={fetchDocs}
                disabled={loading || !canQuery}
                className="w-full px-4 py-2.5 rounded-xl bg-blue-600 text-white font-extrabold hover:bg-blue-700 disabled:opacity-50"
              >
                Buscar
              </button>
            </div>
          </div>

          {!canQuery ? (
            <div className="mt-3 text-sm text-amber-700 font-semibold">
              El contrato es obligatorio.
            </div>
          ) : null}
        </div>

        <div className="p-5 bg-slate-50">
          {err ? (
            <div className="rounded-2xl border border-red-200 bg-red-50 p-4 text-red-700 font-semibold mb-4">
              {err}
            </div>
          ) : null}

          <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
            <div className="grid grid-cols-12 gap-0 px-4 py-3 border-b bg-slate-50 text-xs font-extrabold text-slate-600">
              <div className="col-span-1">Tipo</div>
              <div className="col-span-2">Cliente</div>
              <div className="col-span-2">Fecha</div>
              <div className="col-span-2">Ciudad</div>
              <div className="col-span-1">Pto</div>
              <div className="col-span-2">Factura</div>
              <div className="col-span-1 text-right">Saldo</div>
              <div className="col-span-1 text-right">Abonos</div>
            </div>

            <div className="max-h-[55vh] overflow-y-auto">
              {loading ? (
                <div className="p-6 text-slate-600">Cargando…</div>
              ) : rows.length === 0 ? (
                <div className="p-6 text-slate-500">No hay documentos para ese contrato.</div>
              ) : (
                rows.map((r, idx) => (
                  <div
                    key={`${idx}_${r.tipo}_${r.FACTURA}_${r.FECHAFAC}`}
                    className="grid grid-cols-12 gap-0 px-4 py-3 border-b last:border-b-0 hover:bg-slate-50"
                  >
                    <div className="col-span-1 text-sm font-semibold text-slate-900">
                      {r.tipo || "—"}
                    </div>

                    <div className="col-span-2 text-sm text-slate-800">
                      {r.cliente || "—"}
                    </div>

                    <div className="col-span-2 text-sm text-slate-800">
                      {fmtDate(r.FECHAFAC)}
                    </div>

                    <div className="col-span-2 text-sm text-slate-800">
                      {r.CIUDAD || "—"}
                    </div>

                    <div className="col-span-1 text-sm text-slate-800">
                      {r.pto_emision || "—"}
                    </div>

                    <div className="col-span-2 text-sm text-slate-800 font-medium">
                      {r.FACTURA || "—"}
                    </div>

                    <div className="col-span-1 text-sm text-right font-bold text-slate-900">
                      {fmtMoney(r.saldo)}
                    </div>

                    <div className="col-span-1 text-sm text-right font-bold text-emerald-700">
                      {fmtMoney(r.Abonos)}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {rows.length > 0 ? (
            <div className="mt-4 flex justify-end">
              <div className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700">
                Total documentos: <b className="text-slate-900">{rows.length}</b>
              </div>
            </div>
          ) : null}
        </div>
      </div>

      <style>{`
        @keyframes modalIn {
          from { transform: translateY(6px) scale(.985); opacity: 0; }
          to   { transform: translateY(0) scale(1); opacity: 1; }
        }
      `}</style>
    </div>
  );
}